import React, { useContext, useEffect, useState } from 'react';
import ErrorMessage, { useError } from '../components/ErrorMessage';
import Loading from '../components/Loading';
import * as types from '../types';

interface CartPricingContextValue {
  pricing: types.CartPricing;
  error: string | undefined;
  refreshing: boolean;
}

const CartPricingContext = React.createContext<CartPricingContextValue | undefined>(undefined);
export default CartPricingContext;

interface CartPricingProviderProps {
  children: React.ReactNode;
  fetchPricing: () => Promise<types.CartPricing | undefined>;
  trackError?: (errorMessage: string) => void;
}

export function CartPricingProvider({ children, fetchPricing, trackError }: CartPricingProviderProps) {
  const [cartPricing, setCartPricing] = useState<types.CartPricing | undefined>(undefined);
  // the fetchPricing the displayed pricing came from. derived during render rather than set in the effect, so the
  // render that changes the cart already reports refreshing to children whose effects run before this one
  const [settledFetch, setSettledFetch] = useState<CartPricingProviderProps['fetchPricing'] | undefined>(undefined);
  const refreshing = settledFetch !== fetchPricing;
  const { error, errorID, setError, clearError } = useError();

  useEffect(() => {
    let cancelled = false;
    fetchPricing()
      .then((fetchedPricing) => {
        if (cancelled) {
          return;
        }
        setCartPricing(fetchedPricing);
        setSettledFetch(() => fetchPricing);
        clearError();
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setSettledFetch(() => fetchPricing);
        setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchPricing, setError, clearError]);

  // Initial fetch failed and we have no pricing to render — fall back to a
  // full-screen error since children would have nothing to display. Once we
  // have a pricing snapshot, a subsequent refetch failure must not unmount
  // children: that would force the user to reload (e.g. typing an invalid
  // zip into Stripe's AddressElement triggers a Recurly tax call that
  // throws). Keep the form mounted and let opt-in consumers
  // (useCartPricingError) react to the staleness.
  if (error && !cartPricing) {
    return <ErrorMessage errors={[error]} trackError={trackError} errorID={errorID} />;
  }

  if (!cartPricing) {
    return <Loading />;
  }

  return (
    <CartPricingContext.Provider value={{ pricing: cartPricing, error, refreshing }}>
      {children}
    </CartPricingContext.Provider>
  );
}

export function useCartPricing(): types.CartPricing {
  const ctx = useContext(CartPricingContext);
  if (!ctx) {
    throw new Error('useCartPricing must be used within a CartPricingProvider');
  }

  return ctx.pricing;
}

/**
 * Returns the current pricing-refetch error, if any. When set, the
 * displayed pricing is stale — submit flows should disable their commit
 * action and surfaces showing tax/total should consider blanking them.
 */
export function useCartPricingError(): string | undefined {
  const ctx = useContext(CartPricingContext);
  if (!ctx) {
    throw new Error('useCartPricingError must be used within a CartPricingProvider');
  }

  return ctx.error;
}

/**
 * True while the cart has changed and its new pricing hasn't arrived, so the displayed pricing belongs to the
 * previous cart. False outside a provider.
 */
export function useCartPricingRefreshing(): boolean {
  return useContext(CartPricingContext)?.refreshing ?? false;
}
